import './BetbookComment.less';

import clsx from 'clsx';
import moment from 'moment';
import React, { useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import PopupNg from '@/ui/PopupNg';
import ArrowDownIcon from '@/icons/ArrowDownIcon';
import ReplyMessageIcon from '@/icons/ReplyMessageIcon';
import i18n from '@i18n';
import BetbookCommentReactions from '@/betbook/components/betbook-reactions/BetbookCommentReactions';
import BetbookCommentEditor from '@/betbook/components/betbook-comment-editor/BetbookCommentEditor';
import BetbookCommentHeader from '@/betbook/components/betbook-comment-header/BetbookCommentHeader';
import BetbookCommentBody from '@/betbook/components/betbook-comment-body/BetbookCommentBody';

import { BetbookOptionsButton } from '@/betbook/views/BetbookPostItem';
import { setCommentedCommentId, toggleCommentRating } from '@/betbook/handlers';
import { accountUserData$, loggedIn$ } from '@/account/subs';
import { commentById$, commentedCommentId$, customerComment$, editCommentId$, redactor$ } from '@/betbook/subs';

function BetbookComment({ className, commentId, subjectId, subjectType, topicStatus }) {
    const [showMore, setShowMore] = useState(false);
    const { subscribe } = useReframe();
    const comment = subscribe(commentById$(commentId));
    if (!comment) return null;
    const userData = subscribe(accountUserData$);
    const loggedIn = subscribe(loggedIn$);
    const editCommentId = subscribe(editCommentId$);
    const commentedCommentId = subscribe(commentedCommentId$);
    const commentTime = moment(comment.commentTime).format('DD.MM.YYYY HH:mm');
    const status = comment.status;
    const invalid =
        status === 'IMPROPER' || status === 'REMOVED' || status === 'REMOVED_BY_AUTHOR' || status === 'NOT_ALLOWED';
    const showOpenMoreOptions = loggedIn && !invalid && editCommentId !== commentId && topicStatus === 'ENABLED';
    const commentText = invalid ? i18n(`betbook.comment.message.${status}`) : comment.text;
    const editing = editCommentId && editCommentId === commentId;
    const own = comment.nickname === userData.nickname;
    const customerComment = subscribe(customerComment$);
    const redactor = subscribe(redactor$);

    function openMoreOptionsDialog() {
        PopupNg.dispatcher.open('betbook/commentsOptions', {
            commentId: comment.id,
            isMyComment: own || (comment.redactor && redactor)
        });
    }
    function onToggleCommentRating(e, rating, customerRating) {
        e.stopPropagation();
        dispatch(toggleCommentRating(comment.id, rating !== customerRating ? rating : 'NULL'));
    }
    function openCommentForm() {
        const id = commentedCommentId === comment.id ? null : comment.id;
        dispatch(setCommentedCommentId(id));
    }
    return (
        <>
            <div className={clsx('betbook-comment', { className, redactor: comment.redactor })}>
                <BetbookCommentHeader
                    className={clsx({ own })}
                    avatarId={comment.avatarId}
                    nickname={comment.nickname}
                    date={commentTime}
                    redactor={comment.redactor}
                >
                    {showOpenMoreOptions ? (
                        <div className="options">
                            <BetbookOptionsButton onClick={openMoreOptionsDialog} />
                        </div>
                    ) : null}
                </BetbookCommentHeader>
                <BetbookCommentBody invalid={invalid} text={commentText} editingCommentId={editing && editCommentId} />
                {invalid || editing ? null : (
                    <div className="betbook-comment-stats">
                        <BetbookCommentReactions
                            commentCount={comment.children.length}
                            customerComment={customerComment}
                            onRatingClick={onToggleCommentRating}
                            onCommentClick={openCommentForm}
                            own={own}
                            ratings={comment.ratings}
                            isInDetail
                        />
                        {comment.status === 'EDITED' ? `•\u00A0\u00A0(${i18n('betbook.comment.edited')})` : null}
                        {topicStatus === 'ENABLED' && (
                            <button onClick={openCommentForm} data-atid="comment-reply">
                                <ReplyMessageIcon />
                            </button>
                        )}
                    </div>
                )}
            </div>
            {commentedCommentId !== comment.id || invalid || editing ? null : (
                <BetbookCommentEditor subjectId={subjectId} subjectType={subjectType} parentCommentId={comment.id} />
            )}
            <div className="betbook-comment-replies">
                {comment.children.length ? (
                    <button className="betbook-comment-more" onClick={() => setShowMore(!showMore)}>
                        <ArrowDownIcon className={clsx({ up: !showMore })} />
                        {showMore
                            ? `${i18n('betbook.comments.showAllAnswers')} (${comment.children.length})`
                            : i18n('betbook.comments.hideAnswers')}
                    </button>
                ) : null}
                {showMore
                    ? null
                    : comment.children.map(id => (
                          <BetbookComment
                              key={id}
                              commentId={id}
                              subjectId={subjectId}
                              subjectType={subjectType}
                              topicStatus={topicStatus}
                          />
                      ))}
            </div>
        </>
    );
}

export default BetbookComment;
